/* 
Copyright© Celligence International, LLC - 2026
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import axiosCall from '../services/axios';
import useAbortController from '../common/hooks/useAbortController';
import useIPData from '../common/hooks/useIPData';
import useQidList from '../common/hooks/useQidList';

type CaptureLoginAuditRequest = {
  eventType: string;
  eventResult: string;
  failureReason?: string;
  emailAddress: string;
  webUserId: string;
};

type LoginAuditValue = {
  captureLoginAudit: (audithRequest: CaptureLoginAuditRequest) => Promise<void>;
};

type LoginAuditProviderProps = {
  children: ReactNode;
};

const LoginAuditContext = createContext<LoginAuditValue | null>(null);

const useLoginAuditContext = () => {
  const loginAuditContext = useContext(LoginAuditContext);

  if (loginAuditContext === null) {
    throw new Error('useLoginAuditContext must be inside a LoginAuditProvider');
  }

  return loginAuditContext;
};

const getBrowserKeyword = (): string => {
  const userAgent = navigator.userAgent;

  if (/Edg/i.test(userAgent)) {
    return 'EDGE';
  }

  if (/Firefox/i.test(userAgent)) {
    return 'FIREFOX';
  }

  if (/Chrome|CriOS/i.test(userAgent)) {
    return 'CHROME';
  }

  if (/Safari/i.test(userAgent)) {
    return 'SAFARI';
  }

  if (userAgent) {
    return 'OTHER';
  }

  return 'UNKNBROW';
};

const getClientOSKeyword = (): string => {
  const userAgent = navigator.userAgent;

  if (/Windows/i.test(userAgent)) {
    return 'WINDOWS';
  }

  if (/Android/i.test(userAgent)) {
    return 'ANDROID';
  }

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'IOS';
  }

  if (/CrOS/i.test(userAgent)) {
    return 'CHROMEOS';
  }

  if (/Macintosh|Mac OS X/i.test(userAgent)) {
    return 'MACOS';
  }

  if (/Linux/i.test(userAgent)) {
    return 'LINUX';
  }

  if (userAgent) {
    return 'OTHER';
  }

  return 'UNKNOWN';
};

type QIDOption = {
  code: string;
  keyword?: string;
  description: string;
};

const getQIDCode = (options: QIDOption[], keyword: string, fallbackKeyword?: string): string => {
  const matchingOption = options.find((option) => option.keyword === keyword);

  if (matchingOption?.code) {
    return matchingOption.code;
  }

  if (fallbackKeyword) {
    return options.find((option) => option.keyword === fallbackKeyword)?.code ?? '';
  }

  return '';
};

const getIPVersion = (ipAddress: string): string => {
  if (!ipAddress) return '';

  return ipAddress.includes(':') ? '6' : '4';
};

const LoginAuditProvider = ({ children }: LoginAuditProviderProps): JSX.Element => {
  const { get } = useAbortController();
  const { processIP } = useIPData();
  const { qid: eventTypeQID } = useQidList('TBY1_CD.012');
  const { qid: eventResultQID } = useQidList('TBY1_CD.013');
  const { qid: failureReasonQID } = useQidList('TBY1_CD.014');
  const { qid: browserQID } = useQidList('TBY1_CD.017');
  const { qid: clientOSQID } = useQidList('TBY1_CD.018');

  const eventTypeCodeMap = useMemo(
    () => new Map(eventTypeQID.filter((option) => option.keyword).map((option) => [option.keyword!, option.code])),
    [eventTypeQID],
  );

  const eventResultCodeMap = useMemo(
    () => new Map(eventResultQID.filter((option) => option.keyword).map((option) => [option.keyword!, option.code])),
    [eventResultQID],
  );

  const failureReasonCodeMap = useMemo(
    () => new Map(failureReasonQID.filter((option) => option.keyword).map((option) => [option.keyword!, option.code])),
    [failureReasonQID],
  );

  const captureLoginAudit = useCallback(
    async ({
      eventType,
      eventResult,
      failureReason,
      emailAddress,
      webUserId,
    }: CaptureLoginAuditRequest): Promise<void> => {
      if (!emailAddress) return;

      const eventDateTime = Math.floor(Date.now() / 1000);

      const eventTypeCode = eventTypeCodeMap.get(eventType);
      const eventResultCode = eventResultCodeMap.get(eventResult);
      const failureReasonCode = failureReason ? failureReasonCodeMap.get(failureReason) : '';

      const browser = getQIDCode(browserQID, getBrowserKeyword(), 'UNKNBROW');
      const clientOS = getQIDCode(clientOSQID, getClientOSKeyword(), 'UNKNOWN');

      const ipResp = await processIP();
      const ipAddress = ipResp?.ip || '';
      const ipVersion = getIPVersion(ipAddress);

      const request = {
        url: process.env.REACT_APP_CREATE_BORROWER_LOGIN_AUDIT,
        method: 'post',
        data: {
          webUserID: webUserId,
          eventDateTime,
          email: emailAddress,
          eventType: eventTypeCode,
          eventResult: eventResultCode,
          failureReason: failureReasonCode,
          browser,
          clientOS,
          IPAddress: ipAddress,
          IPVersion: ipVersion,
          sourceApp: 'RCS',
        },
      };

      const config = get();

      try {
        await axiosCall(request, config, {}, true, { redirectOnUnauthorized: false });
      } catch (error: any) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('Error creating borrower login audit record:', error);
        }
      }
    },
    [get, processIP, eventTypeQID, eventResultQID, failureReasonQID, browserQID, clientOSQID],
  );

  return (
    <LoginAuditContext.Provider
      value={{
        captureLoginAudit,
      }}
    >
      {' '}
      {children}{' '}
    </LoginAuditContext.Provider>
  );
};

export { LoginAuditProvider, useLoginAuditContext };
